import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from '../ApiSearchableSelect'
import { EmptyState } from '../ui/empty-state'
import { EnumPropertyInput, getBooleanOptions, getPropertyDefinitionName } from '../properties/PropertyInputUtils'
import { apiClient } from '../../api/client'
import type { PropertyDefinition } from '../../hooks/useQueries'

interface Row {
  code: string
  raw: string
}

interface Props {
  org: string
  /** `user` property definitions. */
  definitions: PropertyDefinition[]
  /** Codes the form already shows with their own field. */
  excludeCodes: string[]
  /** Called with the typed values and whether one of them is invalid. */
  onChange: (values: Record<string, unknown>, invalid: boolean) => void
}

// Raw input string to the value sent to the API.
function toValue(def: PropertyDefinition, raw: string): unknown {
  switch (def.data_type) {
    case 'boolean':
      return raw === 'true'
    case 'number': {
      const n = parseFloat(raw)
      return raw !== '' && !isNaN(n) ? n : raw
    }
    case 'enum':
      return def.properties?.multiple === true ? raw.split(',').map(v => v.trim()).filter(Boolean) : raw
    default:
      return raw
  }
}

// "Add a property" section of the contact creation form: only the defined
// `user` properties, each with the input of its type and the same server-side
// validation as any other property value.
export function CreateContactProperties({ org, definitions, excludeCodes, onChange }: Props) {
  const { t, i18n } = useTranslation('users')
  const { t: tCommon } = useTranslation('common')
  const [rows, setRows] = useState<Row[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [pickerKey, setPickerKey] = useState(0)

  const onChangeRef = useRef(onChange)
  useEffect(() => { onChangeRef.current = onChange })

  const byCode = useMemo(() => new Map(definitions.map(d => [d.code, d])), [definitions])

  useEffect(() => {
    const values: Record<string, unknown> = {}
    for (const row of rows) {
      const def = byCode.get(row.code)
      if (def && row.raw !== '') values[row.code] = toValue(def, row.raw)
    }
    onChangeRef.current(values, rows.some(r => Boolean(errors[r.code])))
  }, [rows, errors, byCode])

  const options: ApiSearchableSelectOption[] = useMemo(() => {
    const taken = new Set([...excludeCodes, ...rows.map(r => r.code)])
    return definitions
      .filter(d => d.code && !taken.has(d.code) && !d.flags?.includes('system_managed'))
      .map(d => ({ value: d.code as string, label: getPropertyDefinitionName(d, d.code, i18n.language) }))
  }, [definitions, excludeCodes, rows, i18n.language])

  const search = async (query: string) =>
    options.filter(o => o.label.toLowerCase().includes(query.toLowerCase())).slice(0, 50)

  const setRaw = (code: string, raw: string) => {
    setRows(prev => prev.map(r => (r.code === code ? { ...r, raw } : r)))
    setErrors(prev => ({ ...prev, [code]: '' }))
  }

  const validate = async (code: string, raw: string) => {
    const def = byCode.get(code)
    if (!def?.uid || raw === '') return
    try {
      const res = await apiClient.POST('/api/v1/organizations/{orgCode}/properties/{propertyUid}/validate', {
        params: { path: { orgCode: org, propertyUid: def.uid } },
        // A number goes as text so the range rules apply.
        body: { value: def.data_type === 'number' ? raw : (toValue(def, raw) as string | boolean | string[]) },
      })
      if (res.data && res.data.status !== 'ok') {
        const message = (res.data as { errors?: string[] }).errors?.join('; ') || t('create.property_invalid')
        setErrors(prev => ({ ...prev, [code]: message }))
      }
    } catch {
      // The API checks again on create.
    }
  }

  const renderInput = (def: PropertyDefinition, row: Row) => {
    const testId = `property-value-${row.code}`
    const invalid = Boolean(errors[row.code])
    const onBlur = () => validate(row.code, row.raw)
    switch (def.data_type) {
      case 'boolean':
        return (
          <select
            value={row.raw}
            onChange={e => { setRaw(row.code, e.target.value); validate(row.code, e.target.value) }}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
            data-testid={testId}
          >
            <option value="">--</option>
            {getBooleanOptions(def.properties?.semantic as string | undefined, tCommon).map(o => (
              <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
            ))}
          </select>
        )
      case 'enum':
        return (
          <EnumPropertyInput
            propertyDefinitionUid={def.uid}
            value={row.raw}
            onChange={v => { setRaw(row.code, v); validate(row.code, v) }}
            org={org}
            testId={testId}
          />
        )
      case 'date':
        return (
          <Input
            type={def.properties?.with_time === true ? 'datetime-local' : 'date'}
            value={row.raw}
            onChange={e => setRaw(row.code, e.target.value)}
            onBlur={onBlur}
            aria-invalid={invalid}
            data-testid={testId}
          />
        )
      case 'number':
        return (
          <Input
            type="number"
            step={def.properties?.integer_only ? '1' : 'any'}
            value={row.raw}
            onChange={e => setRaw(row.code, e.target.value)}
            onBlur={onBlur}
            aria-invalid={invalid}
            data-testid={testId}
          />
        )
      default:
        return (
          <Input
            type="text"
            value={row.raw}
            onChange={e => setRaw(row.code, e.target.value)}
            onBlur={onBlur}
            aria-invalid={invalid}
            data-testid={testId}
          />
        )
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-4">
        <h2 className="text-lg font-semibold text-foreground">{t('create.properties_section')}</h2>
        <div className="w-64" data-testid="add-property-button">
          <ApiSearchableSelect
            key={pickerKey}
            value=""
            onValueChange={() => {}}
            onOptionSelect={o => {
              setRows(prev => [...prev, { code: o.value, raw: '' }])
              setPickerKey(k => k + 1)
            }}
            onSearch={search}
            initialOptions={options}
            placeholder={t('create.add_property')}
            searchPlaceholder={t('create.search_property')}
            emptyMessage={t('create.no_property_definitions')}
            className="w-full"
            data-testid="property-picker"
          />
        </div>
      </div>

      {rows.map(row => {
        const def = byCode.get(row.code)
        if (!def) return null
        return (
          <div key={row.code} className="space-y-1" data-testid={`property-row-${row.code}`}>
            <label className="block text-sm font-medium text-foreground">
              {getPropertyDefinitionName(def, row.code, i18n.language)}
            </label>
            <div className="flex gap-2 items-start">
              <div className="flex-1">{renderInput(def, row)}</div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => {
                  setRows(prev => prev.filter(r => r.code !== row.code))
                  setErrors(prev => ({ ...prev, [row.code]: '' }))
                }}
                data-testid={`remove-property-${row.code}`}
              >
                {t('create.remove_property')}
              </Button>
            </div>
            {errors[row.code] && (
              <p className="text-sm text-destructive" data-testid={`property-error-${row.code}`}>{errors[row.code]}</p>
            )}
          </div>
        )
      })}

      {rows.length === 0 && (
        <EmptyState variant="inline" className="py-3" description={t('create.no_custom_properties')} />
      )}
    </div>
  )
}
